// Angular and Core
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkTreeModule } from '@angular/cdk/tree';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

// Shared
import { BlockableDivModule } from '../shared/components/blockable-div/blockable-div.module';
import { LanguageDialogModule } from '../shared/components/language/language.dialog.module';
import { SharedModule } from '../shared/shared.module';

// Primeng modules
import { AccordionModule } from 'primeng/accordion';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BadgeModule } from 'primeng/badge';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from "primeng/inputswitch";
import { InputTextModule } from 'primeng/inputtext';
import { KnobModule } from 'primeng/knob';
import { ListboxModule } from 'primeng/listbox';
import { MenuModule } from 'primeng/menu';
import { MultiSelectModule } from 'primeng/multiselect';
import { RadioButtonModule } from 'primeng/radiobutton';
import { PanelModule } from 'primeng/panel';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RippleModule } from 'primeng/ripple';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SliderModule } from 'primeng/slider';
import { StepperModule } from 'primeng/stepper';
import { TreeModule } from 'primeng/tree';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TreeTableModule } from 'primeng/treetable';

// Components
import { AccessLogComponent } from './components/access-log/access-log.component';
import { AcquireBoardUserDialogComponent } from './components/acquire-board-user-dialog/acquire-board-user.dialog.component';
import { AddAutomationDialogComponent } from './components/add-automation-dialog/add-automation.dialog.component';
import { AddDeviceDialogComponent } from './components/add-device-dialog/add-device.dialog.component';
import { BoardUserDeleteDialogComponent } from './components/board-user-delete-dialog/board-user-delete.dialog.component';
import { BoardUserDialogComponent } from './components/board-user-dialog/board-user.dialog.component';
import { BoardUsersComponent } from './components/board-users/board-users.component';
import { BoardUsersFileComponent } from './components/board-users-files/board-users-files.component';
import { BoardUsersFilesDialogComponent } from './components/board-users-files-dialog/board-users-files-dialog.component';
import { BoardUsersTableComponent } from './components/board-users-table/board-users-table.component';
import { CallAccessLogComponent } from './components/call-access-log/call-access-log.component';
import { CommandAliasAutomationDialogComponent } from './components/command-alias-automation-dialog/command-alias-automation-dialog.component';
import { CommandAliasDialogComponent } from './components/command-alias-dialog/command-alias-dialog.component';
import { CommandsComponent } from './components/commands/commands.component';
import { ControlAccessComponent } from './components/control-access/control-access-tab/control-access-tab.component';
import { ControlAccessGroupEasyDialogDialogComponent } from './components/control-access/control-access-group-easy-dialog/control-access-group-easy-dialog.component';
import { ControlAccessGroupEasyListComponent } from './components/control-access/control-access-group-easy-list/control-access-group-easy-list.component';
import { ControlAccessSpecialPlanningDialogComponent } from './components/control-access/control-access-special-planning-dialog/control-access-special-planning-dialog.component';
import { ControlAccessSpecialPlanningListComponent } from './components/control-access/control-access-special-planning-list/control-access-special-planning-list.component';
import { ControlAccessWeeklyScheduleDialogComponent } from './components/control-access/control-access-weekly-schedule-dialog/control-access-weekly-schedule-dialog.component';
import { ControlAccessWeeklyScheduleListComponent } from './components/control-access/control-access-weekly-schedule-list/control-access-weekly-schedule-list.component';
import { DatetimeDeviceComponent } from './components/datetime-device/datetime-device.component';
import { DeviceButtonBarComponent } from './components/device-button-bar/device-button-bar.component';
import { DeviceComponent } from './components/device/device.component';
import { DeviceIconComponent } from './components/device-icon/device-icon.component';
import { DeviceInfoComponent } from './components/device-info/device-info.component';
import { DeviceProfilesComponent } from './components/device-profiles/device-profiles.component';
import { DeviceSpecialDayDialogComponent } from './components/device-special-day-dialog/device-special-day-dialog.component';
import { DeviceStateProgressComponent } from './components/device-state-progress/device-state-progress.component';
import { DeviceTabGroupComponent } from './components/device-tab-group/device-tab-group.component';
import { DeviceTabInfoComponent } from './components/device-tab-info/device-tab-info.component';
import { DeviceTimerDialogComponent } from './components/device-timer-dialog/device-timer-dialog.component';
import { DeviceVirtualComponent } from './components/device-virtual/device-virtual.component';
import { EditDeviceDialogComponent } from './components/edit-device-dialog/edit-device.dialog.component';
import { EditFinalUsersDialogComponent } from './components/edit-final-users-dialog/edit-final-users-dialog.component';
import { EditInputDialogComponent } from './components/edit-input-dialog/edit-input.dialog.component';
import { EditOutputDialogComponent } from './components/edit-output-dialog/edit-output.dialog.component';
import { ExportDeviceDialogComponent } from './components/import-export/export-dialog/export-dialog.component';
import { ExportFinalUsersDialogComponent } from './components/export-final-users-dialog/export-final-users-dialog.component';
import { FinalUsersComponent } from './components/final-users/final-users.component';
import { FinalUsersProfilesComponent } from './components/final-users-profiles/final-users-profiles.component';
import { ImportDeviceDialogComponent } from './components/import-export/import-dialog/import-dialog.component';
import { ImportExportComponent } from './components/import-export/import-export-tab/import-export-tab.component';
import { ImportExportListComponent } from './components/import-export/import-export-list/import-export-list.component';
import { ImportFileDialogComponent } from './components/import-file-dialog/import-file-dialog.component';
import { ImportFinalUsersComponent } from './components/import-final-users/import-final-users.component';
import { ImportFinalUsersDialogComponent } from './components/import-final-users-dialog/import-final-users-dialog.component';
import { InputsComponent } from './components/inputs/inputs.component';
import { IoCommandsComponent } from './components/io-commands/io-commandscomponent';
import { MtmaCloneDialogComponent } from './components/mtma-clone-dialog/mtma-clone-dialog.component';
import { MtmaContactDialogComponent } from './components/mtma-manage-contact-dialog/mtma-manage-contact-dialog.component';
import { MtmaControlAccessComponent } from './components/mtma-control-access/mtma-control-access-tab/mtma-control-access-tab.component';
import { MtmaControlAccessGroupEasyDialogDialogComponent } from './components/mtma-control-access/mtma-control-access-group-easy-dialog/mtma-control-access-group-easy-dialog.component';
import { MtmaControlAccessGroupEasyListComponent } from './components/mtma-control-access/mtma-control-access-group-easy-list/mtma-control-access-group-easy-list.component';
import { MtmaControlAccessSpecialPlanningDialogComponent } from './components/mtma-control-access/mtma-control-access-special-planning-dialog/mtma-control-access-special-planning-dialog.component';
import { MtmaControlAccessSpecialPlanningListComponent } from './components/mtma-control-access/mtma-control-access-special-planning-list/mtma-control-access-special-planning-list.component';
import { MtmaControlAccessWeeklyScheduleDialogComponent } from './components/mtma-control-access/mtma-control-access-weekly-schedule-dialog/mtma-control-access-weekly-schedule-dialogcomponent';
import { MtmaControlAccessWeeklyScheduleListComponent } from './components/mtma-control-access/mtma-control-access-weekly-schedule-list/mtma-control-access-weekly-schedule-list.component';
import { MtmaDtmfToneDialogComponent } from './components/mtma-manage-dtmf-tone-dialog/mtma-manage-dtmf-tone-dialog.component';
import { MtmaExportDeviceDialogComponent } from './components/import-export/mtma-export-dialog/mtma-export-dialog.component';
import { MtmaGenericEntryInfoDialogComponent } from './components/mtma-generic-entry-info-dialog/mtma-generic-entry-info.dialog.component';
import { MtmaImportDeviceDialogComponent } from './components/import-export/mtma-import-dialog/mtma-import-dialog.component';
import { MtmaImportExportComponent } from './components/import-export/mtma-import-export-tab/mtma-import-export-tab.component';
import { MtmaImportExportListComponent } from './components/import-export/mtma-import-export-list/mtma-import-export-list.component';
import { MtmaKeypadDialogComponent } from './components/mtma-manage-keypad-dialog/mtma-manage-keypad-dialog.component';
import { MtmaManageContactComponent } from './components/mtma-manage-contact/mtma-manage-contact.component';
import { MtmaManageDtmfToneComponent } from './components/mtma-manage-dtmf-tone/mtma-manage-dtmf-tone.component';
import { MtmaManageKeypadComponent } from './components/mtma-manage-keypad/mtma-manage-keypad.component';
import { MtmaManageModule8CComponent } from './components/mtma-manage-module-8c/mtma-manage-module-8c.component';
import { MtmaManageModuleAcComponent } from './components/mtma-manage-module-ac/mtma-manage-module-ac.component';
import { MtmaManageModuleAddComponent } from './components/mtma-manage-module-add/mtma-manage-module-add.component';
import { MtmaManageModuleBfvsComponent } from './components/mtma-manage-module-bfvs/mtma-manage-module-bfvs.component';
import { MtmaManageModuleComponent } from './components/mtma-manage-module/mtma-manage-module.component';
import { MtmaManageModuleDyComponent } from './components/mtma-manage-module-dy/mtma-manage-module-dy.component';
import { MtmaManageModuleInfoComponent } from './components/mtma-manage-module-info/mtma-manage-module-info.component';
import { MtmaManageModuleKbComponent } from './components/mtma-manage-module-kb/mtma-manage-module-kb.component';
import { MtmaManageModuleRfidComponent } from './components/mtma-manage-module-rfid/mtma-manage-module-rfid.component';
import { MtmaManageTagComponent } from './components/mtma-manage-tag/mtma-manage-tag.component';
import { MtmaManageUnitComponent } from './components/mtma-manage-unit/mtma-manage-unit.component';
import { MtmaRuleAccessDialogComponent } from './components/mtma-rule-access-dialog/mtma-rule-access-dialog.component';
import { MtmaRuleDialogComponent } from './components/mtma-rule-dialog/mtma-rule-dialog.component';
import { MtmaRulesAccessComponent } from './components/mtma-rules-access/mtma-rules-access.component';
import { MtmaRulesAccessRulesComponent } from './components/mtma-rules-access-rules/mtma-rules-access-rules.component';
import { MtmaRulesAccessTimeslicesComponent } from './components/mtma-rules-access-timeslices/mtma-rules-access-timeslices.component';
import { MtmaTagDialogComponent } from './components/mtma-manage-tag-dialog/mtma-manage-tag-dialog.component';
import { MtmaTimeSliceDialogComponent } from './components/mtma-time-slice-dialog/mtma-time-slice-dialog.component';
import { MtmaUnitContactDialogComponent } from './components/mtma-manage-unit-contact-dialog/mtma-manage-unit-contact-dialog.component';
import { MtmaUnitDialogComponent } from './components/mtma-manage-unit-dialog/mtma-manage-unit-dialog.component';
import { NotificationsLegacyMtmaComponent } from './components/notifications-legacy-mtma/notifications-legacy-mtma.component';
import { OutputsComponent } from './components/outputs/outputs.component';
import { PasswordDialogComponent } from './components/password-dialog/password-dialog.component';
import { PasswordEditDialogComponent } from './components/password-edit-dialog/password-edit.dialog.component';
import { RbeLogAccessComponent } from './components/rbe-log-access/rbe-log-access.component';
import { RemoteDevicesComponent } from './components/remote-devices/remote-devices.component';
import { RetryInitComponent } from './components/retry-init/retry-init.component';
import { SafetyInputsComponent } from './components/safety-inputs/safety-inputs.component';
import { SettingsComponent } from './components/settings/settings.component';
import { ShareDeviceDialogComponent } from './components/share-device-dialog/share-device.dialog.component';
import { SignalLevelComponent } from './components/signal-level/signal-level.component';
import { SupportAutomationComponent } from './components/support-automation/support-automation.component';
import { TasksDialogComponent } from './components/tasks-dialog/tasks-dialog.component';
import { ThermoCommandsComponent } from './components/thermo-commands/thermo-commands.component';
import { ThermoExportLogDialogComponent } from './components/thermo-export-log-dialog/thermo-export-log.dialog.component';
import { ThermoLogComponent } from './components/thermo-log/thermo-log.component';
import { TimersListSpecialDayComponent } from './components/timers-list-special-day/timers-list-special-day.component';
import { TimersListTimersComponent } from './components/timers-list-timers/timers-list-timers.component';
import { UpdateFirmwareDialogComponent } from './components/update-firmware-dialog/update-firmware.dialog.component';
import { VirtualDeviceComponent } from './components/virtual-device/virtual-device.component';
import { AutomationsAliasDialogComponent } from './components/automations-alias-dialog/automations-alias-dialog.component';
import { MtmaManagePermissionComponent } from './components/mtma-manage-permissions/mtma-manage-permissions.component';
import { NotificationsDeviceTabComponent } from './components/notifications-device-tab/notifications-device-tab.component';
import { RbeGenericEntryInfoDialogComponent } from './components/rbe-generic-entry-info-dialog/rbe-generic-entry-info.dialog.component';
import { ThermoStatsComponent } from './components/thermo-stats/thermo-stats.component';
import { MtmaConfirmImportDialogComponent } from './components/mtma-confirm-import-dialog/mtma-confirm-import.dialog.component';
import { MtmaImportFileDialogComponent } from './components/import-export/mtma-import-file-dialog/mtma-import-file.dialog.component';
import { InputTextareaModule } from 'primeng/inputtextarea';


@NgModule({
    declarations: [
        AccessLogComponent,
        AcquireBoardUserDialogComponent,
        AddAutomationDialogComponent,
        AddDeviceDialogComponent,
        BoardUserDeleteDialogComponent,
        BoardUserDialogComponent,
        BoardUsersComponent,
        BoardUsersFileComponent,
        BoardUsersFilesDialogComponent,
        BoardUsersTableComponent,
        CallAccessLogComponent,
        CommandAliasAutomationDialogComponent,
        CommandAliasDialogComponent,
        CommandsComponent,
        ControlAccessComponent,
        ControlAccessGroupEasyDialogDialogComponent,
        ControlAccessGroupEasyListComponent,
        ControlAccessSpecialPlanningDialogComponent,
        ControlAccessSpecialPlanningListComponent,
        ControlAccessWeeklyScheduleDialogComponent,
        ControlAccessWeeklyScheduleListComponent,
        DatetimeDeviceComponent,
        DeviceButtonBarComponent,
        DeviceComponent,
        DeviceIconComponent,
        DeviceInfoComponent,
        DeviceProfilesComponent,
        DeviceSpecialDayDialogComponent,
        DeviceStateProgressComponent,
        DeviceTabGroupComponent,
        DeviceTabInfoComponent,
        DeviceTimerDialogComponent,
        DeviceVirtualComponent,
        EditDeviceDialogComponent,
        EditFinalUsersDialogComponent,
        EditInputDialogComponent,
        EditOutputDialogComponent,
        ExportDeviceDialogComponent,
        ExportFinalUsersDialogComponent,
        FinalUsersComponent,
        FinalUsersProfilesComponent,
        ImportDeviceDialogComponent,
        ImportExportComponent,
        ImportExportListComponent,
        ImportFileDialogComponent,
        ImportFinalUsersComponent,
        ImportFinalUsersDialogComponent,
        InputsComponent,
        IoCommandsComponent,
        MtmaCloneDialogComponent,
        MtmaConfirmImportDialogComponent,
        MtmaContactDialogComponent,
        MtmaControlAccessComponent,
        MtmaControlAccessGroupEasyDialogDialogComponent,
        MtmaControlAccessGroupEasyListComponent,
        MtmaControlAccessSpecialPlanningDialogComponent,
        MtmaControlAccessSpecialPlanningListComponent,
        MtmaControlAccessWeeklyScheduleDialogComponent,
        MtmaControlAccessWeeklyScheduleListComponent,
        MtmaDtmfToneDialogComponent,
        MtmaExportDeviceDialogComponent,
        MtmaGenericEntryInfoDialogComponent,
        MtmaImportDeviceDialogComponent,
        MtmaImportExportComponent,
        MtmaImportExportListComponent,
        MtmaImportFileDialogComponent,
        MtmaKeypadDialogComponent,
        MtmaManageContactComponent,
        MtmaManageDtmfToneComponent,
        MtmaManageKeypadComponent,
        MtmaManageModule8CComponent,
        MtmaManageModuleAcComponent,
        MtmaManageModuleAddComponent,
        MtmaManageModuleBfvsComponent,
        MtmaManageModuleComponent,
        MtmaManageModuleDyComponent,
        MtmaManageModuleInfoComponent,
        MtmaManageModuleKbComponent,
        MtmaManageModuleRfidComponent,
        MtmaManageTagComponent,
        MtmaManageUnitComponent,
        MtmaRuleAccessDialogComponent,
        MtmaRuleDialogComponent,
        MtmaRulesAccessComponent,
        MtmaRulesAccessRulesComponent,
        MtmaRulesAccessTimeslicesComponent,
        MtmaTagDialogComponent,
        MtmaTimeSliceDialogComponent,
        MtmaUnitContactDialogComponent,
        MtmaUnitDialogComponent,
        NotificationsLegacyMtmaComponent,
        NotificationsDeviceTabComponent,
        OutputsComponent,
        PasswordDialogComponent,
        PasswordEditDialogComponent,
        RbeGenericEntryInfoDialogComponent,
        RbeLogAccessComponent,
        RemoteDevicesComponent,
        RetryInitComponent,
        SafetyInputsComponent,
        SettingsComponent,
        ShareDeviceDialogComponent,
        SignalLevelComponent,
        SupportAutomationComponent,
        TasksDialogComponent,
        ThermoCommandsComponent,
        ThermoExportLogDialogComponent,
        ThermoLogComponent,
        ThermoStatsComponent,
        TimersListSpecialDayComponent,
        TimersListTimersComponent,
        UpdateFirmwareDialogComponent,
        VirtualDeviceComponent,
        AutomationsAliasDialogComponent,
        MtmaManagePermissionComponent,
    ],
    imports: [
        AccordionModule,
        AutoCompleteModule,
        BadgeModule,
        BlockUIModule,
        BlockableDivModule,
        BreadcrumbModule,
        ButtonModule,
        CalendarModule,
        CardModule,
        CdkTreeModule,
        ChartModule,
        CheckboxModule,
        ChipModule,
        CommonModule,
        ConfirmDialogModule,
        DividerModule,
        DropdownModule,
        FileUploadModule,
        FormsModule,
        ImageModule,
        InputNumberModule,
        InputSwitchModule,
        InputTextareaModule,
        InputTextModule,
        KnobModule,
        LanguageDialogModule,
        ListboxModule,
        MenuModule,
        MultiSelectModule,
        PanelModule,
        ProgressBarModule,
        ProgressSpinnerModule,
        RadioButtonModule,
        ReactiveFormsModule,
        RippleModule,
        RouterModule,
        SelectButtonModule,
        SharedModule,
        SliderModule,
        StepperModule,
        TabViewModule,
        TableModule,
        TagModule,
        ToastModule,
        ToggleButtonModule,
        TooltipModule,
        TranslateModule,
        TreeModule,
        TreeTableModule
    ],
    exports: [
        DeviceComponent,
        DeviceIconComponent,
        DeviceInfoComponent,
        DeviceTabInfoComponent,
    ]
})
export class DevicesModule { }
